import ErrorBoundary from '@ui/error'
import React, {useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import S from '../components/auth/styles/Form.styles'
import SubmitButton from '../components/auth/SubmitButton'
import Layout from '../components/layout/Layout'
import {Select} from '~/src/design-system/BaseComponents'
import {cognitoToDisplay} from '../models/role'
import {roleApi} from '../services'
import {useCurrentUser} from '../store/slice-hooks'
import {setBackendRolePermissions, updateLoggedInAgencyId} from '../store/slices/root-slice'
import {useAppDispatch} from '../store/store-hooks'
import {useLazyGetGaAgencyNamesAndIdsQuery} from '../store/apis/member-access-api'
import {reportCatchError} from '../utils/errorReporter'
import {
  noRolePermissions,
  resolveEffectiveModulePermissions,
} from '../utils/effectiveModulePermissions'
import {getRouteForRole, type ModulePermission} from '../utils/routeUtils'
import {consumePostSignInPath} from '../utils/postSignInPath'

export default function RoleSelectPage(): JSX.Element {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {currentUser} = useCurrentUser()
  const [selectedRole, setSelectedRole] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [getGaAgencyNamesAndIds] = useLazyGetGaAgencyNamesAndIdsQuery()

  const roleOptions = useMemo(
    () =>
      currentUser?.roles.map((role: string) => ({
        value: role,
        label: cognitoToDisplay(role),
      })) ?? [],
    [currentUser?.roles],
  )

  const handleRoleSelect = async () => {
    if (!selectedRole || !currentUser) return

    try {
      setIsLoading(true)
      setError(null)
      const roleName = selectedRole

      // Fetch permissions for the selected role and store in Redux
      const permissionsResponse = await roleApi.getPermissionsForRoles([roleName])
      const primaryRole = permissionsResponse.roles[0]

      if (!primaryRole) {
        // STFFLG-4243: no role found for the picked name: a role granting nothing, never none (with
        // no role in the store the route guard lets every route through)
        dispatch(setBackendRolePermissions(noRolePermissions(roleName)))
        navigate('/manage-companies')
        return
      }

      // Permission resolution (resolveEffectiveModulePermissions, STFFLG-4243):
      // 1. Agency overrides are authoritative — STFFLG-2281 allows escalation
      // 2. User overrides are restrictive (AND) — per-user restrictions only tighten
      // A role with no tree keeps that (empty or absent) tree: the agency's or the user's own tree
      // never stands in for it.
      const rolePermissions = primaryRole.modulePermissions as ModulePermission[] | undefined
      const effectivePermissions =
        resolveEffectiveModulePermissions({
          roleTree: rolePermissions,
          agencyTree: currentUser.agencyAssignments?.[0]?.modulePermissions as
            | ModulePermission[]
            | undefined,
          userTree: currentUser.modulePermissions as ModulePermission[] | undefined,
        }) ?? rolePermissions

      dispatch(
        setBackendRolePermissions({
          _id: primaryRole.id,
          id: primaryRole.id,
          name: primaryRole.name,
          accessList: primaryRole.accessList,
          modulePermissions: effectivePermissions,
          isPlatform: primaryRole.category === 'Platform',
          // Store category for layout variant determination (custom roles need this)
          category: primaryRole.category as 'Platform' | 'MSP' | 'Agency' | 'Company',
          application: 'msp',
          createdDate: primaryRole.createdDate?.toString() ?? '',
          modifiedDate: primaryRole.modifiedDate?.toString() ?? '',
          versionKey: Number(primaryRole.versionKey) || 0,
        }),
      )

      // Store role in localStorage for compatibility
      window.localStorage.setItem('cognitoRole', JSON.stringify({role: primaryRole}))

      // Handle agency selection based on user type:
      // 1. Multi-agency users (agencyAssignments): auto-select the first agency
      // 2. General admin users: fetch their agency list from member access
      const hasMultiAgencyAssignments =
        currentUser.agencyAssignments && currentUser.agencyAssignments.length > 0

      if (hasMultiAgencyAssignments) {
        // Multi-agency user: auto-select the first agency assignment
        const firstAgency = currentUser.agencyAssignments![0]
        dispatch(updateLoggedInAgencyId(firstAgency.agencyId))
      } else if (primaryRole.name.toLowerCase() === 'general_admin') {
        await loadGeneralAdminAgencies(currentUser.id)
      }

      // Get route based on role NAME and effective permissions (with overrides applied)
      const routingPermissions =
        effectivePermissions ?? (primaryRole.modulePermissions as ModulePermission[])
      // STFFLG-4163: a multi-role user reaches here instead of the sign-in pages' own navigate,
      // so this is where a signing link that brought them to sign in is honoured
      // (rememberPostSignInPath in PrivateRoute).
      const targetRoute =
        consumePostSignInPath() ??
        getRouteForRole(primaryRole.category ?? primaryRole.name, routingPermissions)
      navigate(targetRoute)
    } catch (err) {
      setError(err as Error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadGeneralAdminAgencies = async (userId: string) => {
    try {
      const agencies = await getGaAgencyNamesAndIds(userId).unwrap()
      if (agencies.length > 0) {
        dispatch(updateLoggedInAgencyId(agencies[0].id))
      }
    } catch (agencyError) {
      reportCatchError('Failed to fetch agencies for general_admin:', agencyError)
    }
  }

  return (
    <ErrorBoundary error={error}>
      <Layout variant="platform">
        <S.MaxWidth>
          <S.InnerPaper elevation={6}>
            <S.Greeting data-testid="greeting" />
            <S.InnerBox>
              <S.Container
                onSubmit={async (e: React.FormEvent) => {
                  e.preventDefault()
                  await handleRoleSelect()
                }}>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  placeholder="-- Select Role --"
                  options={roleOptions}
                  data-testid="role-select-dropdown"
                />
                <SubmitButton disabled={isLoading}>Select Role</SubmitButton>
              </S.Container>
            </S.InnerBox>
          </S.InnerPaper>
        </S.MaxWidth>
      </Layout>
    </ErrorBoundary>
  )
}
